import { Link } from 'react-router-dom'
import Artwork from './Artwork.jsx'
import Avatar from './Avatar.jsx'
import InlineAlert from './InlineAlert.jsx'
import Markdown from './Markdown.jsx'
import ScorePill from './ScorePill.jsx'
import { METHOD_LABELS, STATE_LABELS } from '../lib/score.js'

/**
 * A whole clause rather than a noun, so `plural` cannot do it alone: one track
 * is "the only track", not "every one of the 1 tracks".
 */
function completedAlbumSentence(n) {
  return n === 1 ? 'The only track, rated.' : `Every one of the ${n} tracks, rated.`
}

/**
 * One row of the feed. Five event types, one shape (MOCKUP/FeedCard.jsx).
 *
 * The card is the same object in every case — who, what, and the thing it was
 * about — and only the sentence between "who" and "the thing" changes. That
 * is what stops a feed of mixed events reading as five different products.
 *
 * Nothing here is a copy. The score is the actor's current one, joined live on
 * the server, and the review body is the review's. An edit upstream shows on
 * the next load rather than leaving a stale card behind.
 */
const SENTENCES = {
  rating_published: 'rated',
  review_published: 'reviewed',
  list_published: 'started a list',
  completed_album: 'finished',
  follow: 'followed',
}

function Subject({ media }) {
  if (!media) return null
  const path = media.kind === 'song' ? `/song/${media.public_id}` : `/album/${media.public_id}`
  return (
    <Link to={path} className="feed-subject">
      <Artwork
        art={media.artwork_url ? { url: media.artwork_url } : null}
        title={media.title}
        artist={media.credit_name}
        size={56}
      />
      <span className="row-main">
        <span className="feed-subject-title">{media.title}</span>
        <span className="footnote">
          {media.credit_name}
          {media.year ? ` · ${media.year}` : ''}
        </span>
      </span>
    </Link>
  )
}

export default function FeedCard({ card, error, onReact }) {
  const actor = card.actor
  const sentence = SENTENCES[card.event_type] || 'posted'
  const isList = card.event_type === 'list_published'
  const isFollow = card.event_type === 'follow'

  return (
    <article className="card card-pad feed-card fade-up">
      <header className="feed-head">
        <Link to={`/profile/${actor?.username}`} className="feed-actor">
          <Avatar
            name={actor?.display_name || actor?.username}
            avatarUrl={actor?.avatar_url}
            size={36}
          />
          <span className="row-main">
            <span className="feed-actor-name">{actor?.display_name || actor?.username}</span>
            <span className="footnote">
              @{actor?.username} · {sentence}
            </span>
          </span>
        </Link>
        {card.score ? (
          <ScorePill
            score={card.score.score}
            label={`${actor?.display_name || 'Their'} score`}
          />
        ) : null}
      </header>

      {isList ? (
        <Link to={`/lists/${card.user_list?.public_id}`} className="feed-list">
          <span className="feed-subject-title">{card.user_list?.title}</span>
          <span className="footnote">
            {card.user_list?.item_count || 0}{' '}
            {card.user_list?.item_count === 1 ? 'entry' : 'entries'}
          </span>
        </Link>
      ) : isFollow ? (
        <p className="footnote mb-0">
          {actor?.display_name || actor?.username} is following someone new.
        </p>
      ) : (
        <Subject media={card.media} />
      )}

      {card.event_type === 'completed_album' && card.payload?.track_count ? (
        <p className="footnote mt-2 mb-0">{completedAlbumSentence(card.payload.track_count)}</p>
      ) : null}

      {card.score?.method && card.score.method !== 'direct' ? (
        <p className="footnote mt-1 mb-0">{METHOD_LABELS[card.score.method]}</p>
      ) : null}
      {card.score?.state ? (
        <p className="footnote mt-1 mb-0">{STATE_LABELS[card.score.state]}</p>
      ) : null}

      {card.review ? (
        card.review.spoiler ? (
          <Link to={`/reviews/${card.review.public_id}`} className="feed-spoiler">
            A review, marked as a spoiler. Open it to read.
          </Link>
        ) : (
          <Link to={`/reviews/${card.review.public_id}`} className="feed-review">
            <Markdown html={card.review.body_html} className="feed-review-body" />
          </Link>
        )
      ) : null}

      <footer className="feed-foot">
        <button
          type="button"
          className={`icon-btn ${card.reacted ? 'on' : ''}`}
          onClick={() => onReact?.(card)}
          aria-pressed={card.reacted}
          aria-label={card.reacted ? 'Remove your heart' : 'Heart this'}
        >
          <span aria-hidden="true">♥</span> {card.reaction_count || 0}
        </button>
        {card.review ? (
          <Link to={`/reviews/${card.review.public_id}`} className="footnote">
            {card.review.comment_count || 0}{' '}
            {card.review.comment_count === 1 ? 'comment' : 'comments'}
          </Link>
        ) : null}
      </footer>

      <InlineAlert>{error}</InlineAlert>
    </article>
  )
}
